import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupReorder — deterministic cross-unit consolidated reorder data for
// ONE group (Ship 6). The group analogue of useGroupMenuEngineering: a
// service-role edge-fn read (get-group-reorder-snapshot) gated by
// resolveGroupOwner, so the group OWNER sees every unit's below-par items
// even when they aren't a member of that unit (the RLS-asymmetry fix the
// scatter hook documents applies identically here).
//
// Returns:
//   items   — every below-par item across the group, urgency-sorted
//   perUnit — { [restaurant_id]: { restaurant_name, items_below_par,
//             critical_count, restock_dollars } }
//   units   — [{ restaurant_id, restaurant_name }], name-sorted
//   totals  — { total_items_below_par, total_restock_dollars, critical_count }
//   loading, error, refresh
//
// DECOUPLED from the AI brief (useGroupReorderBrief): the list renders from
// these rows whether or not the brief has loaded. No cache — a plain read
// each time, same as useGroupKpi.

const EMPTY_TOTALS = {
  total_items_below_par: 0,
  total_restock_dollars: 0,
  critical_count: 0,
}

export function useGroupReorder(groupId) {
  const [items, setItems]     = useState([])
  const [perUnit, setPerUnit] = useState({})
  const [units, setUnits]     = useState([])
  const [totals, setTotals]   = useState(EMPTY_TOTALS)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  // Synchronous in-flight guard — set before the first await, cleared in
  // finally. Stops two quick mounts/refreshes from double-firing.
  const inFlightRef = useRef(false)

  const fetchReorder = useCallback(async () => {
    // Null/undefined groupId → do NOT fire (no bad request).
    if (!groupId) {
      setItems([])
      setPerUnit({})
      setUnits([])
      setTotals(EMPTY_TOTALS)
      setLoading(false)
      setError(null)
      return
    }
    if (inFlightRef.current) return

    inFlightRef.current = true
    setLoading(true)
    setError(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/get-group-reorder-snapshot`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ group_id: groupId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const payload = await resp.json()
      if (payload?.error) {
        throw new Error(payload.message || 'Could not load group reorder data.')
      }
      setItems(Array.isArray(payload?.items) ? payload.items : [])
      setPerUnit(payload?.per_unit && typeof payload.per_unit === 'object' ? payload.per_unit : {})
      setUnits(Array.isArray(payload?.units) ? payload.units : [])
      setTotals({
        total_items_below_par: Number(payload?.total_items_below_par || 0),
        total_restock_dollars: Number(payload?.total_restock_dollars || 0),
        critical_count:        Number(payload?.critical_count || 0),
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupReorder] reorder fetch failed', e)
      setError('Could not load group reorder data.')
      setItems([])
      setPerUnit({})
      setUnits([])
      setTotals(EMPTY_TOTALS)
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }, [groupId])

  // Initial load — kick off once groupId resolves.
  useEffect(() => {
    fetchReorder()
  }, [fetchReorder])

  // The edge fn already returns units name-sorted + deduped. Fall back to
  // deriving from items defensively.
  const derivedUnits = useMemo(() => {
    if (units.length > 0) return units
    const byId = new Map()
    for (const it of items) {
      if (it.restaurant_id && !byId.has(it.restaurant_id)) {
        byId.set(it.restaurant_id, {
          restaurant_id: it.restaurant_id,
          restaurant_name: it.restaurant_name || 'Unit',
        })
      }
    }
    return [...byId.values()].sort((a, b) =>
      (a.restaurant_name || '').localeCompare(b.restaurant_name || ''),
    )
  }, [units, items])

  return {
    items,
    perUnit,
    units: derivedUnits,
    totals,
    loading,
    error,
    refresh: () => fetchReorder(),
  }
}
